import React, { memo, useCallback, useState } from "react";
import type { FC, ReactNode } from "react";
import { IconDownFill, IconSelect } from "~/components";
import { fetchOrderList } from "~/api/endpoints";
import TradeHistoryItem from "./TradeHistoryItem";
import { TradeOrder, TradeToken } from "~/types";
import { Placeholder } from "@telegram-apps/telegram-ui";
import { useTranslation } from "react-i18next";
import { ORDER_DIRECTION, ORDER_STATUS } from "../../constants";
import useSWR from "swr";
import { themeParams, useSignal } from "@telegram-apps/sdk-react";
import clsx from "clsx";
import useCurrentTokenOnly from "../hooks/useCurrentTokenOnly";

interface IProps {
    children?: ReactNode;
    tradeOrderId?: string;
    token: TradeToken;
    onReverseTrade: (order: TradeOrder) => void;
}

const TradeHistory: FC<IProps> = ({ token, tradeOrderId, onReverseTrade }) => {
    const { t } = useTranslation();
    const isDark = useSignal(themeParams.isDark);
    const [visible, setVisible] = useState(false);
    const options = [
        { id: "all", name: t("all") },
        { id: String(ORDER_DIRECTION.buy), name: t("buy") },
        { id: String(ORDER_DIRECTION.sell), name: t("sell") },
    ];

    const { isCurrentTokenOnly, toggleCurrentTokenOnly } =
        useCurrentTokenOnly();

    const [selectedOption, setSelectedOption] = useState<{
        id: string;
        name: string;
    }>(options[0]);

    const queryOrderList = useCallback(
        ([
            _,
            selectedOptionId,
            isCurrentTokenOnly,
            chain_id,
            contract,
            _tradeOrderId
        ]: any) => {
            if (isCurrentTokenOnly == null) return [];

            const params: {
                direction?: string;
                chain_id?: string;
                contract?: string;
            } = {};

            if (selectedOptionId !== "all") {
                params.direction = selectedOptionId;
            }

            if (isCurrentTokenOnly) {
                if (chain_id == null || contract == null) return [];

                (params.chain_id = chain_id), (params.contract = contract);
            }

            return fetchOrderList(params).then((res) => res?.data?.list ?? []);
        },
        []
    );

    const { data: orderList, isLoading: loading } = useSWR(
        [
            "TradePage:queryOrderList",
            selectedOption.id,
            isCurrentTokenOnly,
            token?.token_base?.chain_id,
            token?.token_base?.contract,
            tradeOrderId,
        ],
        queryOrderList,
        {
            fallbackData: [],
            keepPreviousData: true,
            refreshInterval(latestData) {
                const hasPendindOrder = (latestData ?? []).some(
                    (order) => order.status === ORDER_STATUS.PROGRESS
                );
                return hasPendindOrder ? 3000 : 0;
            },
        }
    );

    const renderDropdownOption = (
        option: { id: string; name: string },
        isLast: boolean
    ) => (
        <div
            key={option.id} // 使用id作为key属性
            onClick={() => handleOptionClick(option)}
            className={`option cursor-pointer flex justify-between items-center text-text_1 pr-2 ml-2 py-2 my-[2px] bg-bg_0 relative`} // 根据是否为最后一个选项添加边框
        >
            {option.name}
            {selectedOption.id === option.id && (
                <IconSelect className="text-[16px] text-brand_1" />
            )}
            {/* 仅当选中时显示图标 */}
            {!isLast && (
                <div className="absolute bottom-0 -right-0.5 h-[0.5px] w-full bg-border_2" />
            )}
        </div>
    );

    const handleOptionClick = (option: { id: string; name: string }) => {
        setSelectedOption(option); // 设置选中的选项
        setVisible(false); // 关闭弹框
        console.log(`${option.name} clicked`); // 打印选中的选项
    };

    const isSelect = isCurrentTokenOnly === true;

    return (
        <div>
            <div className="h-11 px-4 text-text_1 text-body1 font-Semibold flex items-center">
                <span>{t("activity")}</span>
            </div>
            <div className="flex justify-between px-4 border-b border-border_1 items-center pb-2.5">
                <div
                    className="flex cursor-pointer items-center h-[28px]"
                    onClick={toggleCurrentTokenOnly}
                >
                    <span
                        className={clsx(
                            "w-3.5 h-3.5 flex-none rounded-full mr-1 flex items-center justify-center",
                            isSelect
                                ? "bg-button_brand"
                                : "border border-border_2"
                        )}
                    >
                        {isSelect && (
                            <IconSelect className="text-White text-direction1 scale-90" />
                        )}
                    </span>
                    <span className="h-3.5 text-text_2 text-direction1">
                        {t("current_token_only")}
                    </span>
                </div>
                <div className="text-text_1 text-body4 bg-bg_1 rounded-[4px] flex items-center relative">
                    <button
                        className="flex items-center px-2 py-1"
                        onClick={() => setVisible(!visible)}
                    >
                        <span className="font-Regular">
                            {selectedOption.name}
                        </span>
                        <IconDownFill className="ml-1" />
                    </button>
                    {visible && (
                        <div
                            className="absolute z-10 right-0 w-[150px] bg-bg_0 rounded-[8px] p-[2px] border-[0.5px] border-border_2 mt-1 top-full"
                            style={{
                                boxShadow: `0px 8px 20px 0px rgba(0, 0, 0, ${isDark ? "0.4" : "0.1"})`,
                            }}
                        >
                            {options.map((option, index) =>
                                renderDropdownOption(
                                    option,
                                    index === options.length - 1
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>

            {!loading && orderList.length === 0 ? (
                <>
                    <Placeholder
                        header={t("no_activity")}
                        className="text-text_2 py-16 text-body1"
                        style={
                            {
                                "--tgui--title3--font_size": "16px",
                            } as React.CSSProperties
                        }
                    ></Placeholder>
                </>
            ) : (
                <>
                    {orderList.map((order) => (
                        <TradeHistoryItem
                            key={`${order.order_id}/${order.status}/${order.income.value}/${order.income.change}`}
                            order={order}
                            onReverseTrade={onReverseTrade}
                        />
                    ))}
                </>
            )}
        </div>
    );
};

export default memo(TradeHistory);
